import { useEffect, useRef, useState } from "react";
import { settingsApi } from "./api";
import { createRequestGenerationGate } from "./request-generation";
import { SkeletonGrid, SkeletonLines } from "./Skeleton";
import type { InteractionBlockDetail, InteractionBlockSummary } from "./types";

function timeLabel(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export function InteractionRecords() {
  const [blocks, setBlocks] = useState<InteractionBlockSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [characterFilter, setCharacterFilter] = useState("");
  const [filterCharacters, setFilterCharacters] = useState<Array<{ id: string; name: string }>>([]);
  const listRequests = useRef(createRequestGenerationGate());
  const currentFilter = useRef("");
  const [detail, setDetail] = useState<InteractionBlockDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailSummary, setDetailSummary] = useState<InteractionBlockSummary | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const detailRequests = useRef(createRequestGenerationGate());

  function load(cursor?: string, append = false, filter = currentFilter.current): void {
    const generation = listRequests.current.begin();
    setLoading(true);
    setError("");
    void settingsApi.loadInteractions(cursor, filter || undefined).then((page) => {
      if (!listRequests.current.isCurrent(generation)) return;
      setBlocks((current) => append ? [...current, ...page.blocks.filter(block => !current.some(existing => existing.id === block.id))] : page.blocks);
      setNextCursor(page.next_cursor);
      if (page.filter_characters) setFilterCharacters(page.filter_characters);
    }).catch(() => { if (listRequests.current.isCurrent(generation)) setError("无法读取互动记录，请稍后重试。"); })
      .finally(() => { if (listRequests.current.isCurrent(generation)) setLoading(false); });
  }

  useEffect(() => { load(); return () => { listRequests.current.invalidate(); detailRequests.current.invalidate(); }; }, []);

  function changeCharacter(value: string): void {
    currentFilter.current = value;
    setCharacterFilter(value);
    setBlocks([]);
    setNextCursor(null);
    closeDetail();
    load(undefined, false, value);
  }

  useEffect(() => {
    if (!detail && !detailLoading && !detailError) return;
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") { event.preventDefault(); closeDetail(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not(:disabled), [tabindex]:not([tabindex='-1'])")];
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown); restoreTarget?.focus(); };
  }, [detail, detailLoading, detailError]);

  function openDetail(block: InteractionBlockSummary): void {
    const generation = detailRequests.current.begin();
    setDetail(null);
    setDetailError("");
    setDetailSummary(block);
    setDetailLoading(true);
    void settingsApi.loadInteraction(block.id).then((value) => {
      if (detailRequests.current.isCurrent(generation) && value.id === block.id) setDetail(value);
      else if (detailRequests.current.isCurrent(generation)) setDetailError("互动记录响应不匹配，请重试。");
    }).catch(() => { if (detailRequests.current.isCurrent(generation)) setDetailError("无法读取这段互动记录。"); })
      .finally(() => { if (detailRequests.current.isCurrent(generation)) setDetailLoading(false); });
  }

  function closeDetail(): void {
    detailRequests.current.invalidate();
    setDetail(null);
    setDetailError("");
    setDetailLoading(false);
    setDetailSummary(null);
  }

  return <div className="interaction-stack">
    <label className="interaction-character-filter">筛选角色<select aria-label="筛选交互记录角色" value={characterFilter} onChange={(event) => changeCharacter(event.target.value)}>
      <option value="">全部角色</option>
      {filterCharacters.map(character => <option key={character.id} value={character.id}>{character.name}</option>)}
    </select></label>
    <aside className="privacy-note">这里只显示匿名代号与对话正文；正文仍可能包含参与者主动透露的信息。</aside>
    {error ? <div className="panel-state"><p className="action-error" role="alert">{error}</p><button className="secondary outlined" type="button" onClick={() => load()}>重试</button></div> : null}
    {loading && blocks.length === 0 ? <SkeletonGrid items={6} variant="row" label="正在读取互动记录" /> : !error && blocks.length === 0 ? <p className="panel-state">{characterFilter ? "这个角色还没有可查看的互动记录。" : "还没有可查看的互动记录。"}</p> : <div className="interaction-list">
      {blocks.map((block) => <button className="interaction-row" type="button" key={block.id} onClick={() => openDetail(block)}>
        <span><strong>{block.character_name}</strong><small>{timeLabel(block.started_at)}</small></span>
        <span>{block.message_count} 条消息</span>
      </button>)}
    </div>}
    {blocks.length > 0 ? <div className="interaction-page-state">{loading ? <SkeletonGrid items={2} variant="row" label="正在加载更多互动记录" /> : nextCursor ? <button className="secondary outlined" type="button" onClick={() => load(nextCursor, true)}>加载更多</button> : "已经到底了"}</div> : null}
    {detail || detailLoading || detailError ? <div className="interaction-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDetail(); }}>
      <section ref={dialogRef} className="interaction-dialog" role="dialog" aria-modal="true" aria-labelledby="interaction-dialog-title">
        <header><div><h3 id="interaction-dialog-title">{detail?.character_name ?? "互动记录"}</h3>{detail ? <p>{timeLabel(detail.started_at)} · 保留至 {timeLabel(detail.expires_at)}</p> : null}</div><button ref={closeRef} type="button" aria-label="关闭互动详情" onClick={closeDetail}>×</button></header>
        {detailLoading ? <SkeletonLines lines={7} label="正在读取互动详情" /> : detailError ? <div className="panel-error" role="alert"><p>{detailError}</p>{detailSummary ? <button className="secondary outlined" type="button" onClick={() => openDetail(detailSummary)}>重试</button> : null}</div> : <div className="chat-blocks">
          {detail?.messages.map((message, index) => <article className={`chat-block ${message.role}`} key={`${message.created_at}-${index}`}>
            <header><strong>{message.speaker}</strong><time>{timeLabel(message.created_at)}</time></header>
            <p>{message.content}</p>
          </article>)}
        </div>}
      </section>
    </div> : null}
  </div>;
}
